import React, { FC, ElementType, MouseEvent, useState, useEffect } from 'react';
import { Collapse, CollapseProps } from 'reactstrap';
import _includes from 'lodash/includes';
import styled, { StyledComponent } from 'styled-components';
import Icon from '@minecraft.icon';
import { useTranslation } from '@blocs.i18n';

export const COLLAPSE_TEST_ID = 'COLLAPSE_TEST_ID';

export const TOGGLER_TEST_ID = 'TOGGLER_TEST_ID';

export const TOGGLE_ICON_TEST_ID = 'TOGGLE_ICON_TEST_ID';

const StyledTogglerContainer = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  border-bottom: 1px solid ${(props): string => props.theme.colors.primary.ltGrey1};
  justify-content: space-between;
  padding: 0.25rem 1rem 0.25rem 0.5rem;
`;

const StyledTogglerItem = styled.div`
  align-items: center;
  display: flex;
  color: ${(props): string => props.theme.typography.body.color};
`;

const StyledTogglerTitle = styled.p`
  color: ${(props): string => props.theme.typography.body.color};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 0;
`;

const StyledCollapse = styled(Collapse)`
  margin: 0.5rem 1rem 0.5rem 0.5rem;
`;

const IconBorder = styled.div`
  border: 0.0625rem solid ${(props): string => props.theme.colors.primary.darkGrey};
  border-radius: 0.125rem;
`;

const StyledChevronIcon = styled(Icon)`
  margin-left: 1rem;
`;

const StyledIcon = styled(StyledChevronIcon)`
  padding: 0.3rem;
`;

export type Props = {
  isDefaultOpen?: boolean;
  borderedIcon?: boolean;
  noMargin?: boolean;
  title?: string;
  className?: string;
  'data-qa-id'?: string;
  render?: () => React.ReactNode;
  toggle?: () => void;
  children?: React.ReactNode;
  headerActions?: React.ReactNode;
};

type IToggler<P> = FC<P> & {
  StyledTogglerContainer: StyledComponent<'div', any>;
  StyledTogglerItem: StyledComponent<'div', any>;
  StyledTogglerTitle: StyledComponent<'div', any>;
  StyledCollapse: StyledComponent<ElementType, CollapseProps>;
  StyledChevronIcon: StyledComponent<FC<P>, any>;
};

/**
 * @deprecated: use CollapsePanel from @minecraft.molecules instead
 * this component is difficult to style and use without complex overrides
 * that are difficult to maintain without breaking existing implementations
 */
const Toggler: IToggler<Props> = ({
  isDefaultOpen = false,
  borderedIcon = false,
  noMargin = false,
  title,
  className,
  'data-qa-id': qaId,
  render,
  toggle,
  children,
  headerActions,
}) => {
  const [isOpen, setOpen] = useState<boolean>(isDefaultOpen);
  const { t } = useTranslation();
  const handleToggle = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;

    if (_includes(target.classList, 'checkmark') || target.nodeName === 'INPUT') return;

    toggle && toggle();

    setOpen(!isOpen);
  };

  const renderIcon = (): JSX.Element => {
    const name = isOpen ? 'chevron-open-thick' : 'chevron-closed-thick';

    return borderedIcon ? (
      <IconBorder data-testid={TOGGLE_ICON_TEST_ID}>
        <StyledIcon name={name} inheritColor />
      </IconBorder>
    ) : (
      <StyledChevronIcon name={name} inheritColor />
    );
  };

  useEffect(() => {
    setOpen(isDefaultOpen);
  }, [isDefaultOpen]);

  const statusIcon = renderIcon();

  return (
    <div className={className}>
      <StyledTogglerContainer
        role="button"
        aria-label={isOpen ? t('common:button.close') : t('common:button.open')}
        onClick={handleToggle}
        data-testid={TOGGLER_TEST_ID}
        aria-expanded={isOpen}
      >
        <StyledTogglerItem>
          {render && render()}
          {title && <StyledTogglerTitle>{title}</StyledTogglerTitle>}
        </StyledTogglerItem>
        <StyledTogglerItem data-qa-id={qaId}>
          <>
            {headerActions}
            {statusIcon}
          </>
        </StyledTogglerItem>
      </StyledTogglerContainer>
      {noMargin ? (
        <Collapse isOpen={isOpen} data-testid={COLLAPSE_TEST_ID}>
          {children}
        </Collapse>
      ) : (
        <StyledCollapse isOpen={isOpen} data-testid={COLLAPSE_TEST_ID}>
          {children}
        </StyledCollapse>
      )}
    </div>
  );
};

Toggler.StyledTogglerContainer = StyledTogglerContainer;
Toggler.StyledTogglerItem = StyledTogglerItem;
Toggler.StyledTogglerTitle = StyledTogglerTitle;
Toggler.StyledCollapse = StyledCollapse;
Toggler.StyledChevronIcon = StyledChevronIcon;

export default Toggler;
